import { useLogout, useGetIdentity } from '@refinedev/core';
import { NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AdminPageTransition } from '../components/PageTransition';
import { PlatformKitManager } from './platform-kit-integration';

interface Identity {
	id: string;
	name: string;
	email: string;
	avatar?: string;
	rememberMe?: boolean;
}

export const AdminLayout = () => {
	const { mutate: logout } = useLogout();
	const { data: identity } = useGetIdentity<Identity>();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [showPlatformKit, setShowPlatformKit] = useState(false);

	// Check if mobile on mount and resize
	useEffect(() => {
		const checkMobile = () => {
			const mobile = window.innerWidth < 1024; // lg breakpoint
			setIsMobile(mobile);
			if (mobile) {
				setSidebarCollapsed(true);
			}
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	// Close mobile menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				mobileMenuOpen &&
				!(event.target as Element).closest('.mobile-sidebar')
			) {
				setMobileMenuOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [mobileMenuOpen]);

	const navigationContent = (
		<nav className='space-y-2'>
			<NavLink
				to='/admin'
				end
				onClick={() => isMobile && setMobileMenuOpen(false)}
				className={({ isActive }) =>
					`flex items-center ${
						sidebarCollapsed && !isMobile
							? 'justify-center px-3'
							: 'space-x-3 px-3'
					} py-3 rounded-xl transition-all duration-200 group ${
						isActive
							? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
							: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
					}`
				}
				title={sidebarCollapsed && !isMobile ? 'Dashboard' : ''}>
				<svg
					className='w-5 h-5 flex-shrink-0'
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z'
					/>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z'
					/>
				</svg>
				{(!sidebarCollapsed || isMobile) && (
					<span className='font-medium'>Dashboard</span>
				)}
			</NavLink>

			<NavLink
				to='/admin/projects'
				onClick={() => isMobile && setMobileMenuOpen(false)}
				className={({ isActive }) =>
					`flex items-center ${
						sidebarCollapsed && !isMobile
							? 'justify-center px-3'
							: 'space-x-3 px-3'
					} py-3 rounded-xl transition-all duration-200 group ${
						isActive
							? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
							: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
					}`
				}
				title={sidebarCollapsed && !isMobile ? 'Projects' : ''}>
				<svg
					className='w-5 h-5 flex-shrink-0'
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
					/>
				</svg>
				{(!sidebarCollapsed || isMobile) && (
					<span className='font-medium'>Projects</span>
				)}
			</NavLink>

			<NavLink
				to='/admin/media'
				onClick={() => isMobile && setMobileMenuOpen(false)}
				className={({ isActive }) =>
					`flex items-center ${
						sidebarCollapsed && !isMobile
							? 'justify-center px-3'
							: 'space-x-3 px-3'
					} py-3 rounded-xl transition-all duration-200 group ${
						isActive
							? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/25'
							: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
					}`
				}
				title={sidebarCollapsed && !isMobile ? 'Media Library' : ''}>
				<svg
					className='w-5 h-5 flex-shrink-0'
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
					/>
				</svg>
				{(!sidebarCollapsed || isMobile) && (
					<span className='font-medium'>Media Library</span>
				)}
			</NavLink>

			<NavLink
				to='/admin/content'
				onClick={() => isMobile && setMobileMenuOpen(false)}
				className={({ isActive }) =>
					`flex items-center ${
						sidebarCollapsed && !isMobile
							? 'justify-center px-3'
							: 'space-x-3 px-3'
					} py-3 rounded-xl transition-all duration-200 group ${
						isActive
							? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white shadow-lg shadow-emerald-500/25'
							: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
					}`
				}
				title={sidebarCollapsed && !isMobile ? 'Content' : ''}>
				<svg
					className='w-5 h-5 flex-shrink-0'
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V7a2 2 0 00-2-2H7a2 2 0 00-2 2v4'
					/>
				</svg>
				{(!sidebarCollapsed || isMobile) && (
					<span className='font-medium'>Content</span>
				)}
			</NavLink>

			<NavLink
				to='/admin/fields'
				onClick={() => isMobile && setMobileMenuOpen(false)}
				className={({ isActive }) =>
					`flex items-center ${
						sidebarCollapsed && !isMobile
							? 'justify-center px-3'
							: 'space-x-3 px-3'
					} py-3 rounded-xl transition-all duration-200 group ${
						isActive
							? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white shadow-lg shadow-emerald-500/25'
							: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
					}`
				}
				title={sidebarCollapsed && !isMobile ? 'Fields' : ''}>
				<svg
					className='w-5 h-5 flex-shrink-0'
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4'
					/>
				</svg>
				{(!sidebarCollapsed || isMobile) && (
					<span className='font-medium'>Fields</span>
				)}
			</NavLink>

			{/* Platform Kit Manager Button */}
			<button
				onClick={() => setShowPlatformKit(true)}
				className={`flex items-center w-full ${
					sidebarCollapsed && !isMobile
						? 'justify-center px-3'
						: 'space-x-3 px-3'
				} py-3 rounded-xl transition-all duration-200 group text-slate-700 hover:bg-slate-100 hover:text-slate-900`}
				title={sidebarCollapsed && !isMobile ? 'Platform Manager' : ''}>
				<svg
					className='w-5 h-5 flex-shrink-0'
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
					/>
				</svg>
				{(!sidebarCollapsed || isMobile) && (
					<span className='font-medium'>Platform Manager</span>
				)}
			</button>
		</nav>
	);

	return (
		<div className='min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 compact-admin text-sm'>
			{/* Mobile Header */}
			<div className='lg:hidden fixed top-0 left-0 right-0 h-12 bg-white border-b border-slate-200 shadow-sm z-40 flex items-center justify-between px-3'>
				<button
					onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					className='p-2 rounded-lg hover:bg-slate-100 transition-colors'>
					<svg
						className='w-6 h-6 text-slate-600'
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'>
						<path
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth={2}
							d='M4 6h16M4 12h16M4 18h16'
						/>
					</svg>
				</button>

				<div className='flex items-center space-x-3'>
					<div className='w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center'>
						<span className='text-white font-bold text-sm'>W</span>
					</div>
					<h1 className='font-bold text-slate-800 text-lg'>Admin Panel</h1>
				</div>

				<button
					onClick={() => logout()}
					className='p-2 rounded-lg hover:bg-slate-100 transition-colors'>
					<svg
						className='w-5 h-5 text-slate-600'
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'>
						<path
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth={2}
							d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
						/>
					</svg>
				</button>
			</div>

			{/* Mobile Overlay */}
			{mobileMenuOpen && (
				<div className='lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30 transition-opacity'></div>
			)}

			{/* Desktop Sidebar */}
			<div
				className={`hidden lg:block fixed top-0 left-0 h-screen z-30 transition-all duration-300 ease-in-out ${
					sidebarCollapsed ? 'w-16' : 'w-60'
				} bg-white border-r border-slate-200 shadow-xl`}>
				{/* Header */}
				<div
					className={`flex items-center ${
						sidebarCollapsed ? 'justify-center p-3' : 'justify-between p-4'
					} border-b border-slate-100`}>
					{!sidebarCollapsed && (
						<div className='flex items-center space-x-3'>
							<div className='w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center'>
								<span className='text-white font-bold text-sm'>W</span>
							</div>
							<div>
								<h1 className='font-bold text-slate-800 text-lg'>
									Admin Panel
								</h1>
								<p className='text-slate-500 text-xs'>Content Management</p>
							</div>
						</div>
					)}
					<button
						onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
						className='p-2 rounded-lg hover:bg-slate-100 transition-colors'
						title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
						<svg
							className='w-5 h-5 text-slate-600'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d={sidebarCollapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'}
							/>
						</svg>
					</button>
				</div>

				{/* Navigation */}
				<div
					className={`flex-1 overflow-y-auto ${
						sidebarCollapsed ? 'p-2' : 'p-3'
					}`}>
					{navigationContent}
				</div>

				{/* User Info - Desktop */}
				<div
					className={`border-t border-slate-100 ${
						sidebarCollapsed ? 'p-2' : 'p-3'
					}`}>
					<div
						className={`flex items-center ${
							sidebarCollapsed ? 'justify-center' : 'justify-between'
						}`}>
						{!sidebarCollapsed && (
							<div className='flex items-center space-x-3'>
								<div className='w-8 h-8 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center'>
									<span className='text-white text-sm font-medium'>
										{identity?.name?.charAt(0) || 'U'}
									</span>
								</div>
								<div>
									<p className='text-sm font-medium text-slate-900'>
										{identity?.name || 'User'}
									</p>
									<p className='text-xs text-slate-500'>Administrator</p>
								</div>
							</div>
						)}
						<button
							onClick={() => logout()}
							className={`p-2 rounded-lg hover:bg-slate-100 transition-colors ${
								sidebarCollapsed ? 'mx-auto' : ''
							}`}
							title='Logout'>
							<svg
								className='w-5 h-5 text-slate-600'
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>

			{/* Mobile Sidebar */}
			<div
				className={`mobile-sidebar lg:hidden fixed top-0 left-0 h-screen z-40 w-64 bg-white border-r border-slate-200 shadow-xl transition-transform duration-300 ease-in-out ${
					mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
				}`}>
				{/* Mobile Header */}
				<div className='flex items-center justify-between p-4 border-b border-slate-100'>
					<div className='flex items-center space-x-3'>
						<div className='w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center'>
							<span className='text-white font-bold text-sm'>W</span>
						</div>
						<div>
							<h1 className='font-bold text-slate-800 text-lg'>Admin Panel</h1>
							<p className='text-slate-500 text-xs'>Content Management</p>
						</div>
					</div>
					<button
						onClick={() => setMobileMenuOpen(false)}
						className='p-2 rounded-lg hover:bg-slate-100 transition-colors'>
						<svg
							className='w-5 h-5 text-slate-600'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M6 18L18 6M6 6l12 12'
							/>
						</svg>
					</button>
				</div>

				{/* Mobile Navigation */}
				<div className='flex-1 p-3 overflow-y-auto'>{navigationContent}</div>

				{/* Mobile User Info */}
				<div className='border-t border-slate-100 p-3'>
					<div className='flex items-center justify-between'>
						<div className='flex items-center space-x-3'>
							<div className='w-8 h-8 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center'>
								<span className='text-white text-sm font-medium'>
									{identity?.name?.charAt(0) || 'U'}
								</span>
							</div>
							<div>
								<p className='text-sm font-medium text-slate-900'>
									{identity?.name || 'User'}
								</p>
								<p className='text-xs text-slate-500'>Administrator</p>
							</div>
						</div>
						<button
							onClick={() => logout()}
							className='p-2 rounded-lg hover:bg-slate-100 transition-colors'
							title='Logout'>
							<svg
								className='w-5 h-5 text-slate-600'
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div
				className={`h-screen flex flex-col transition-all duration-300 ease-in-out ${
					isMobile
						? 'pt-12' // Mobile: account for mobile header
						: sidebarCollapsed
						? 'ml-16' // Desktop collapsed
						: 'ml-60' // Desktop expanded
				}`}>
				<div className='flex-1 w-full h-full text-sm'>
					<AdminPageTransition />
				</div>
			</div>

			{/* Platform Kit Manager Modal */}
			{showPlatformKit && (
				<PlatformKitManager
					projectRef={
						import.meta.env.VITE_SUPABASE_PROJECT_REF || 'canmqhrpllekbpvcdzaw'
					}
					onClose={() => setShowPlatformKit(false)}
				/>
			)}
		</div>
	);
};
